import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DEALERSHIP_INFO, LOGO_PRIMARY, LOGO_SIZES } from '../config/dealership';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const DashboardLoginPage = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/dashboard/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password.trim() }),
      });

      if (!response.ok) {
        throw new Error('Invalid password');
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        if (response.ok) {
          throw new Error('Unexpected response format');
        }
        throw new Error('Invalid password');
      }

      if (data.ok && data.token) {
        sessionStorage.setItem('dashboard_token', data.token);
        navigate('/dashboard');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.message.includes('body') || err.message.includes('clone') || err.message.includes('stream')) {
        setError('Invalid password');
      } else {
        setError(err.message || 'Failed to login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center px-6" data-testid="dashboard-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_PRIMARY}
              alt={DEALERSHIP_INFO.name}
              style={{ height: `${LOGO_SIZES.dashboard}px` }}
              className="w-auto mx-auto mb-6"
              data-testid="dashboard-login-logo"
            />
            <h1 
              className="font-serif text-2xl md:text-3xl text-white mb-2"
              data-testid="login-title"
            >
              Dashboard Access
            </h1>
            <p className="text-sm text-zinc-500">
              Enter password to access the dealer dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 text-red-400"
                data-testid="login-error"
              >
                <AlertCircle size={20} className="shrink-0" />
                <p className="text-sm">{error}</p>
              </motion.div>
            )}

            {/* Password Input */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter dashboard password"
                  className="bg-[#000000] border-white/10 text-white h-12 pl-12 rounded-sm focus:border-[#CCAD60]"
                  data-testid="login-password-input"
                  autoFocus
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#CCAD60] text-[#000000] font-bold uppercase tracking-widest text-xs hover:bg-[#D4B870] transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock size={18} className="mr-2" />
                  Unlock Dashboard
                </>
              )}
            </Button>
          </form>

          {/* Footer Note */}
          <p className="text-center text-xs text-zinc-600 mt-8">
            Protected area for {DEALERSHIP_INFO.name} staff only
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardLoginPage;
